// Pipelines · Activation (TripBook Activation, phase 3). Mirrors
// 51trips-colab-api docs/tripbook-pipelines-activation-endpoint.md. Every
// endpoint here is admin-only. Instants are epoch millis; trip dates are epoch
// millis at UTC midnight.

import {ActivationChannel, ActivationDetail, ActivationRoute, Touchpoint} from './Activation.ts'
import type {ConversionCard} from './ConversionPipeline.ts'

// The two boards behind Pipelines; also the `pipeline` of a My tasks item.
export type PipelineId = 'activation' | 'conversion'

// §1 — column order of the board.
export const CRM_STAGES = [
    'PRE_TRIP',
    'REACHING_OUT',
    'WAITING',
    'EXPIRING_SOON',
    'REGISTERED',
    'LOST_EXPIRED',
    'LOST_CANCELLED',
    'LOST_OPTED_OUT',
    'LOST_WRONG_CONTACT',
] as const
export type CrmStage = typeof CRM_STAGES[number]

export type LostReason = 'CANCELLED' | 'OPTED_OUT' | 'WRONG_CONTACT'
export const LOST_REASONS: LostReason[] = ['CANCELLED', 'OPTED_OUT', 'WRONG_CONTACT']

// The Lost columns a card can be dropped on, and the MARK_LOST reason each one
// means (§3.5). Lost · Expired is derived from the deadline: not a drop target.
export const LOST_STAGE_REASON: Partial<Record<CrmStage, LostReason>> = {
    LOST_CANCELLED: 'CANCELLED',
    LOST_OPTED_OUT: 'OPTED_OUT',
    LOST_WRONG_CONTACT: 'WRONG_CONTACT',
}

export const isFinalCrmStage = (stage: CrmStage | null | undefined) =>
    stage === 'REGISTERED' || (!!stage && stage.startsWith('LOST_'))

// §9.4: 'system' or 'customer'; null on the final columns ('person' is Conversion only).
export type CrmActor = 'system' | 'person' | 'customer' | string | null

export type HumanOutcome = 'SAYS_REGISTERED' | 'WILL_DO_IT' | 'NO_ANSWER' | 'NOT_INTERESTED' | 'WRONG_CONTACT'
export const HUMAN_OUTCOMES: HumanOutcome[] = ['SAYS_REGISTERED', 'WILL_DO_IT', 'NO_ANSWER', 'NOT_INTERESTED', 'WRONG_CONTACT']

export const CRM_CHANNELS: ActivationChannel[] = ['EMAIL', 'SMS', 'WHATSAPP', 'PHONE']

export type CrmAlert = 'CONTACT_FAILED' | 'PAUSED' | 'LATE_ASSIGNMENT' | 'NO_REPLY' | string

// §1.2
export type NextAction = {
    type: 'PERSON' | 'UNASSIGNED' | 'AUTO' | 'WAIT'
    key: string | null
    text: string
    dueAt: number | null
    assigneeId: string | null
    touchpointId: string | null
}

export type CrmStep = {index: number; total: number}

// §3.1
export type BoardCard = {
    id: string
    crmStage: CrmStage
    crmStageAt: number
    route: ActivationRoute
    market: string
    code: string
    codeType: 'UNIQUE' | 'GENERIC'
    agencyId: string
    agencyName: string | null
    accountManagerId: string | null
    traveler: {
        firstName?: string | null
        lastName?: string | null
        email?: string | null
        phone?: string | null
        preferredChannel?: ActivationChannel | null
    }
    trip: {startDate: number | null; endDate: number | null; countries: string[]}
    redemptionDeadline: number | null
    daysToDeadline: number | null
    flags: {contactFailed: boolean; pausedUntil: number | null; contactFailedReason: string | null}
    nextAction: NextAction | null
    step: CrmStep | null
    alerts: CrmAlert[]
    messagesSent: number
    lostReason: LostReason | null
}

export type BoardColumn = {
    stage: CrmStage
    actor: CrmActor
    final: boolean
    count: number
    cards: BoardCard[]
    nextCursor: string | null
}

export type BoardResponse = {
    pipeline: 'activation'
    generatedAt: number
    columns: BoardColumn[]
}

// `assignee`: uids, 'automatic' (nextAction AUTO) and/or 'unassigned'.
// `market` and `assignee` take several values (sent as CSV, §9.5).
export type BoardFilters = {
    market?: string[]
    assignee?: string[]
    agencyId?: string
    accountManager?: string
    route?: 'A' | 'C1'
    from?: number
    to?: number
    q?: string
}

export type BoardQuery = BoardFilters & {stage?: CrmStage; cursor?: string; limit?: number}

// §3.2
export type PreparedMessage = {
    templateKey: string
    channel: ActivationChannel
    language: string
    to: string | null
    subject: string | null
    text: string
    channels: ActivationChannel[]
    // Only with channel WHATSAPP: outside the 24 h window the message has to
    // go as this approved template, with these numbered variables.
    whatsappTemplate?: string | null
    whatsappBody?: Record<string, string> | null
}

export type CrmCommandType = 'TOUCHPOINT_DONE' | 'ADD_FOLLOW_UP' | 'ASSIGN' | 'UPDATE_CONTACT' | 'MARK_LOST' | 'ADD_NOTE' | 'CANCEL' | 'REQUEST_EXTRA'

export type CrmActivationDetail = {
    activation: ActivationDetail
    crm: {
        pipeline: 'ACTIVATION'
        stage: CrmStage
        stageAt: number
        nextAction: NextAction | null
        step: CrmStep | null
        alerts: CrmAlert[]
        messagesSent: number
        lostReason: LostReason | null
        lostAt: number | null
        lostNote: string | null
    }
    agency: {
        id: string
        name: string | null
        accountManagerId: string | null
        accountManagerName: string | null
        optInPreTrip: boolean
    }
    nextTouch: {
        touchpoint: Touchpoint
        position: CrmStep | null
        preparedMessage: PreparedMessage | null
    } | null
    deliverables: {pdfUrl: string | null; activationLink: string | null}
    history: HistoryPage
    allowedCommands: CrmCommandType[]
    outcomes: HumanOutcome[]
    lostReasons: LostReason[]
}

// §3.3
export type HistoryEventType =
    | 'stage_changed' | 'touchpoint_sent' | 'touchpoint_status' | 'touchpoint_done' | 'follow_up_added'
    | 'assigned' | 'contact_updated' | 'contact_failed' | 'unsubscribed' | 'lost' | 'note_added'
    | 'registered' | 'expired' | 'paused' | 'command' | 'replied'

export type HistoryEvent = {
    id: string
    type: HistoryEventType | string
    at: number
    actor: string
    actorName?: string | null
    data?: Record<string, unknown> | null
}

export type HistoryPage = {items: HistoryEvent[]; nextCursor: string | null}

// §3.4 — `assignee`: a uid, 'me' (backend default), 'unassigned' or 'all'.
// Since Conversion (its contract §3.5) every item carries `pipeline` and the
// card of that pipeline; an item without it is an Activation one.
type CrmTaskBase = {
    kind: 'HUMAN_TOUCH' | 'FIX_CONTACT'
    touchpoint: Touchpoint
    overdue: boolean
}
export type CrmTask =
    | CrmTaskBase & {pipeline?: 'activation'; card: BoardCard}
    | CrmTaskBase & {pipeline: 'conversion'; card: ConversionCard}

export const taskPipeline = (task: CrmTask): PipelineId => task.pipeline ?? 'activation'

export type TasksPage = {items: CrmTask[]; nextCursor: string | null}

// GET /crm/tasks filters (§3.4, §9.6). from/to are inclusive days on scheduledAt.
export type TasksQuery = {
    assignee: string
    market?: string[]
    kind?: CrmTask['kind']
    pipeline?: PipelineId | 'all'
    from?: number
    to?: number
}

// §3.5 — the body of POST /crm/activations/{id}/commands.
export type CrmCommand =
    | {commandId?: string; type: 'TOUCHPOINT_DONE'; note?: string; data: {touchpointId?: string; channel: ActivationChannel; outcome: HumanOutcome; date?: number}}
    | {commandId?: string; type: 'ADD_FOLLOW_UP'; note?: string; data: {date: number; channel: ActivationChannel; assignee?: string}}
    | {commandId?: string; type: 'ASSIGN'; note?: string; data: {touchpointId?: string; assignee: string | null}}
    | {commandId?: string; type: 'UPDATE_CONTACT'; note?: string; data: {email?: string | null; phone?: string | null; preferredChannel?: ActivationChannel | '' | null}}
    | {commandId?: string; type: 'MARK_LOST'; note?: string; data: {reason: LostReason}}
    | {commandId?: string; type: 'ADD_NOTE'; note?: string; data: {text: string}}

// §3.6
export type NoteFile = {name: string; url: string; contentType?: string | null; size?: number | null}

export type CrmNote = {
    id: string
    authorId: string
    authorName: string | null
    text: string
    files: NoteFile[]
    pinned: boolean
    createdAt: number
    updatedAt: number
}

export type NotesFilter = 'all' | 'notes' | 'files' | 'pinned'

// §3.7
export type TeamMember = {
    id: string
    name: string
    initials: string | null
    avatarUrl: string | null
    role: string | null
}

// §4
export const CRM_ERROR_COMMAND_NOT_ALLOWED = 'COMMAND_NOT_ALLOWED'
export const CRM_ERROR_INVALID_COMMAND = 'INVALID_COMMAND'
export const CRM_ERROR_NOT_IN_PIPELINE = 'NOT_IN_PIPELINE'
